import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { BounceLoader } from 'react-spinners';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Button from '../../common/components/Button';
import CopyHtml from '../../common/components/CopyHtml';
import { getCopy } from '../../common/lib/copyDefaults';
import { getError } from '../../common/lib/getError';
import { redirectToLogin } from '../../common/lib/redirectToLogin';
import {
  isClaimedByOtherTab,
  subscribeToRegistrationFlowClaim,
} from '../../common/lib/registrationFlowSession';
import {
  verifyEmail,
  resendEmailVerification,
  continueAfterEmailVerification,
} from '../../redux/actions';
import { fetchUser } from '../../services/auth';

import '../../../style/index.css';
import './verifyEmail.css';

const mapStateToProps = (state) => ({
  errorDetail: state.verifyEmailReducer.errorDetail,
  emailVerificationFailed: state.verifyEmailReducer.emailVerificationFailed,
  emailVerificationSuccess: state.verifyEmailReducer.emailVerificationSuccess,
  statusCode: state.verifyEmailReducer.statusCode,
  isVerifying: state.verifyEmailReducer.isVerifying,
  isEmailVerified: state.verifyEmailReducer.isEmailVerified,
  linkExpired: state.verifyEmailReducer.linkExpired,
  isResendingEmail: state.registerReducer.isResendingEmail,
  resendEmailMessage: state.registerReducer.resendEmailMessage,
  resendEmailMessageIsError: state.registerReducer.resendEmailMessageIsError,
  resendCooldownRemaining: state.registerReducer.resendCooldownRemaining,
});

const mapDispatchToProps = {
  verifyEmail,
  resendEmailVerification,
  continueAfterEmailVerification,
};

class VerifyEmailView extends Component {
  static propTypes = {
    history: PropTypes.object.isRequired,
    verifyEmail: PropTypes.func.isRequired,
    resendEmailVerification: PropTypes.func.isRequired,
    continueAfterEmailVerification: PropTypes.func.isRequired,
    isVerifying: PropTypes.bool.isRequired,
    emailVerificationSuccess: PropTypes.bool.isRequired,
    emailVerificationFailed: PropTypes.bool.isRequired,
    errorDetail: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    statusCode: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    linkExpired: PropTypes.bool.isRequired,
    isResendingEmail: PropTypes.bool.isRequired,
    resendEmailMessage: PropTypes.string.isRequired,
    resendEmailMessageIsError: PropTypes.bool.isRequired,
    resendCooldownRemaining: PropTypes.number.isRequired,
  };

  constructor(props) {
    super(props);
    this.handleContinueClick = this.handleContinueClick.bind(this);
    this.handleRegistrationFlowClaim = this.handleRegistrationFlowClaim.bind(this);
    this.handleResendClick = this.handleResendClick.bind(this);
    this.handleLoginClick = this.handleLoginClick.bind(this);
    this.unsubscribeFromRegistrationFlowClaim = null;
  }

  componentDidMount() {
    this.unsubscribeFromRegistrationFlowClaim = subscribeToRegistrationFlowClaim(
      this.handleRegistrationFlowClaim,
    );
    this.props.verifyEmail();
  }

  componentWillUnmount() {
    if (this.unsubscribeFromRegistrationFlowClaim) {
      this.unsubscribeFromRegistrationFlowClaim();
    }
  }

  handleRegistrationFlowClaim() {
    if (isClaimedByOtherTab()) {
      redirectToLogin(this.props.history);
    }
  }

  componentDidUpdate(prevProps) {
    const { emailVerificationSuccess, emailVerificationFailed } = this.props;
    if (
      (emailVerificationSuccess && !prevProps.emailVerificationSuccess) ||
      (emailVerificationFailed && !prevProps.emailVerificationFailed)
    ) {
      requestAnimationFrame(() => {
        const el = document.getElementById('verify-email-heading');
        if (el) {
          el.focus();
        }
      });
    }
  }

  handleContinueClick(e) {
    e.preventDefault();
    const { history, continueAfterEmailVerification } = this.props;
    continueAfterEmailVerification(history.replace);
  }

  handleResendClick(e) {
    e.preventDefault();
    const { isResendingEmail, resendCooldownRemaining, resendEmailVerification, history } =
      this.props;
    if (isResendingEmail || resendCooldownRemaining > 0) {
      return;
    }
    resendEmailVerification(history.push);
  }

  handleLoginClick(e) {
    e.preventDefault();
    redirectToLogin(this.props.history);
  }

  renderExpiredActions(copy) {
    const { accessToken } = fetchUser();
    const {
      isResendingEmail,
      resendEmailMessage,
      resendEmailMessageIsError,
      resendCooldownRemaining,
    } = this.props;
    const resendDisabled = isResendingEmail || resendCooldownRemaining > 0;
    const cooldownMessage =
      resendCooldownRemaining > 0
        ? copy.resendCooldown.replace('{seconds}', String(resendCooldownRemaining))
        : '';

    if (accessToken) {
      return (
        <div className="button-wrap">
          {isResendingEmail ? (
            <div className="loading-wrapper">
              <BounceLoader color="#084267" />
            </div>
          ) : (
            <Button
              label={copy.resendButtonLabel}
              onClickFn={this.handleResendClick}
              className={resendDisabled ? 'nyl-button disabled' : 'nyl-button'}
              disabled={resendDisabled}
              id="verify-email-resend-button"
            />
          )}
          {(resendEmailMessage || cooldownMessage) && (
            <div
              className="verify-email__resend-status"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {resendEmailMessage && (
                <p
                  className={
                    resendEmailMessageIsError ? 'submit-error' : 'verify-email__resend-success'
                  }
                >
                  {resendEmailMessage}
                </p>
              )}
              {cooldownMessage && (
                <p className="verify-email__resend-cooldown">{cooldownMessage}</p>
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="button-wrap">
        <Button
          label={copy.loginButtonLabel}
          onClickFn={this.handleLoginClick}
          className="nyl-button"
          id="verify-email-login-button"
        />
      </div>
    );
  }

  render() {
    const {
      isVerifying,
      emailVerificationSuccess,
      emailVerificationFailed,
      errorDetail,
      statusCode,
      linkExpired,
    } = this.props;
    const copy = getCopy('/verify-email');
    const failureMessage = getError(errorDetail, statusCode) || copy.failureMessage;

    return (
      <div className="verify-email row justify-content-center">
        <div className="col-xl-7 col-lg-8 col-md-8 col-sm-10">
          {isVerifying && (
            <div className="loading-wrapper">
              <BounceLoader color="#084267" />
            </div>
          )}
          {!isVerifying && emailVerificationSuccess && (
            <>
              <div className="verify-email__body">
                <h1 id="verify-email-heading" tabIndex={-1} className="verify-email__title">
                  {copy.header}
                </h1>
                <CopyHtml html={copy.body} className="verify-email__description" />
                <CopyHtml html={copy.line2} className="verify-email__description" />
              </div>
              <div className="button-wrap">
                <Button
                  label={copy.continueLabel}
                  onClickFn={this.handleContinueClick}
                  className="nyl-button"
                  id="verify-email-continue-button"
                />
              </div>
            </>
          )}
          {!isVerifying && emailVerificationFailed && (
            <>
              <div className="verify-email__body">
                <h1
                  id="verify-email-heading"
                  tabIndex={-1}
                  className="verify-email__title verify-email__title--error"
                >
                  {linkExpired ? copy.expiredTitle : 'Verification failed'}
                </h1>
                {linkExpired ? (
                  <CopyHtml html={copy.expiredLoginHelper} className="verify-email__description" />
                ) : (
                  <CopyHtml html={failureMessage} className="verify-email__description" />
                )}
              </div>
              {linkExpired && this.renderExpiredActions(copy)}
            </>
          )}
        </div>
      </div>
    );
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(VerifyEmailView));
